import { normalizeBillingAccessStatus, getBillingAssessmentCreditBalance, isFullAppAccess } from '@process-advisor/process-miner/consultant/billingAccessModel';
// ============================================================================
// aiBillingAccess.js
// Central frontend guard for Metricus Assessment Credit usage.
//
// Usage-plan Forge apps are installed by URL and use Metricus billing credits
// rather than Atlassian Marketplace licensing. The first AI generation attempt
// for a process model/dataset asks the user to authorise Assessment Credits.
// The authorisation is tracked per model/dataset so the user is only asked once.
//
// Credit rule:
//   1 Assessment Credit enables AI content for up to 50,000 work items/tasks.
//   Required credits = ceil(recordCount / 50,000).
// ============================================================================

export const ASSESSMENT_RECORD_LIMIT = 50000;
export const ASSESSMENT_CREDIT_VALID_DAYS = 60;
const ASSESSMENT_CREDIT_VALID_MS = ASSESSMENT_CREDIT_VALID_DAYS * 24 * 60 * 60 * 1000;

const AI_INVOKE_TIMEOUT_MS = 45000;

const withAiInvokeTimeout = (promise, label, timeoutMs = AI_INVOKE_TIMEOUT_MS) => {
  let timer;
  return Promise.race([
    Promise.resolve(promise),
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(`${label} did not respond within ${Math.round(timeoutMs / 1000)} seconds.`)), timeoutMs);
    }),
  ]).finally(() => clearTimeout(timer));
};

let aiBillingContext = {
  AppVersion: '',
  licenseInfo: null,
  planAccess: null,
  customerID: '',
  modelId: '',
  modelTitle: '',
  recordCount: 0,
  sample: 'N',
  category: '',
};

let cachedBillingStatus = null;
let billingStatusListeners = [];

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const formatRecordCount = (value) => toNumber(value, 0).toLocaleString();

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

export const getRequiredAssessmentCredits = (recordCount = 0) => {
  const count = toNumber(recordCount, 0);
  return Math.max(1, Math.ceil(count / ASSESSMENT_RECORD_LIMIT));
};

const getModelKey = (context = getAiBillingAccessContext()) => {
  const modelId = String(context?.modelId || context?.processModelId || '').trim();
  const title = String(context?.modelTitle || '').trim();
  const customer = String(context?.customerID || '').trim();
  return modelId || `${customer || 'customer'}:${title || 'model'}`;
};

const getAuthorizationStorageKey = (context = getAiBillingAccessContext()) => {
  const modelKey = getModelKey(context);
  const customer = String(context?.customerID || 'customer').trim();
  return `metricus:assessment-credit-authorized:${customer}:${modelKey}`;
};

const canUseLocalStorage = () => typeof window !== 'undefined' && window.localStorage;

export const getStoredAssessmentAuthorization = (context = getAiBillingAccessContext()) => {
  if (!canUseLocalStorage()) return null;
  try {
    const raw = window.localStorage.getItem(getAuthorizationStorageKey(context));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.authorized !== true) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

const storeAssessmentAuthorization = (authorization = {}, context = getAiBillingAccessContext(), requirement = {}) => {
  const stored = {
    authorized: true,
    source: authorization?.source || 'assessment-credit',
    modelId: context?.modelId || '',
    modelTitle: context?.modelTitle || '',
    customerID: context?.customerID || '',
    recordCount: requirement?.recordCount || 0,
    recordLimit: ASSESSMENT_RECORD_LIMIT,
    requiredCredits: requirement?.requiredCredits || 1,
    assessmentCreditId: authorization?.assessmentCreditId || authorization?.creditId || null,
    remainingCredits: authorization?.remainingCredits ?? authorization?.availableAssessmentCredits ?? null,
    authorizedAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + ASSESSMENT_CREDIT_VALID_MS).toISOString(),
  };
  if (canUseLocalStorage()) {
    try { window.localStorage.setItem(getAuthorizationStorageKey(context), JSON.stringify(stored)); } catch (_) {}
  }
  if (typeof window !== 'undefined') {
    window.__PROCESS_ADVISOR_AI_CREDIT_AUTHORIZATIONS__ = {
      ...(window.__PROCESS_ADVISOR_AI_CREDIT_AUTHORIZATIONS__ || {}),
      [getAuthorizationStorageKey(context)]: stored,
    };
  }
  return stored;
};

export const subscribeBillingStatus = (listener) => {
  if (typeof listener !== 'function') return () => {};
  billingStatusListeners.push(listener);
  if (cachedBillingStatus) {
    try { listener(cachedBillingStatus); } catch (_) {}
  }
  return () => {
    billingStatusListeners = billingStatusListeners.filter((item) => item !== listener);
  };
};

const publishBillingStatus = (status) => {
  cachedBillingStatus = status;
  if (typeof window !== 'undefined') {
    window.__PROCESS_ADVISOR_BILLING_STATUS__ = status;
    try { window.dispatchEvent(new CustomEvent('metricus-billing-status', { detail: status })); } catch (_) {}
  }
  billingStatusListeners.forEach((listener) => {
    try { listener(status); } catch (_) {}
  });
  return status;
};

export const setAiBillingAccessContext = (context = {}) => {
  aiBillingContext = {
    ...aiBillingContext,
    ...context,
    recordCount: toNumber(context.recordCount ?? aiBillingContext.recordCount, 0),
  };
  if (typeof window !== 'undefined') {
    window.__PROCESS_ADVISOR_AI_BILLING_CONTEXT__ = aiBillingContext;
  }
  return aiBillingContext;
};

export const getAiBillingAccessContext = () => {
  if (typeof window !== 'undefined' && window.__PROCESS_ADVISOR_AI_BILLING_CONTEXT__) {
    return { ...aiBillingContext, ...window.__PROCESS_ADVISOR_AI_BILLING_CONTEXT__ };
  }
  return aiBillingContext;
};


const isSampleDataset = (context = getAiBillingAccessContext()) => {
  const sample = context?.sample ?? context?.isSample ?? context?.sampleDataset;
  const category = String(context?.category || context?.modelCategory || '').trim().toLowerCase();
  return sample === true || sample === 'Y' || sample === 'y' || String(sample).toLowerCase() === 'true' || category === 'sample';
};

const isQuickHealthcheckRequest = (reportLabel = '') => {
  const label = String(reportLabel || '').trim().toLowerCase();
  return label === 'quick healthcheck' || label === 'quick health check';
};

export const requiresAssessmentCreditBilling = (context = getAiBillingAccessContext()) => {
  if (isSampleDataset(context)) return false;
  const plan = String(context?.planAccess?.plan || context?.planAccess?.pricing || 'subscription').toLowerCase();
  const platform = String(context?.AppVersion || '').toUpperCase();
  const isUsageIntegratedPlatform = platform === 'FORGE' || platform === 'SNOW';
  return isUsageIntegratedPlatform && plan === 'usage';
};

export const getAssessmentCreditRequirement = (context = getAiBillingAccessContext()) => {
  const recordCount = toNumber(context?.recordCount, 0);
  const sampleDataset = isSampleDataset(context);
  const requiresBilling = requiresAssessmentCreditBilling(context);
  const requiredCredits = sampleDataset ? 0 : getRequiredAssessmentCredits(recordCount);
  return {
    requiresBilling,
    sampleDataset,
    recordLimit: ASSESSMENT_RECORD_LIMIT,
    recordCount,
    requiredCredits,
    withinLimit: true,
    message: requiresBilling
      ? `${requiredCredits} Assessment Credit${requiredCredits === 1 ? '' : 's'} will enable AI content for this model (${formatRecordCount(recordCount)} work items/tasks).`
      : '',
  };
};

const getAvailableCredits = (status = {}) => getBillingAssessmentCreditBalance(status);

const normalizeBillingStatus = (status = {}) => publishBillingStatus(normalizeBillingAccessStatus(status));

export const getCachedBillingStatus = () => cachedBillingStatus;

const buildAssessmentAuthorizationPayload = ({ context = getAiBillingAccessContext(), requirement = getAssessmentCreditRequirement(context), reportLabel = 'AI content' } = {}) => ({
  datasetId: context.modelId || '',
  processModelId: context.modelId || '',
  modelId: context.modelId || '',
  assessmentKey: context.modelId || '',
  assessmentId: context.modelId || '',
  datasetName: context.modelTitle || '',
  modelTitle: context.modelTitle || '',
  customerID: context.customerID || '',
  workItemCount: requirement.recordCount,
  recordCount: requirement.recordCount,
  recordLimit: requirement.recordLimit,
  requiredCredits: requirement.requiredCredits,
  creditCount: requirement.requiredCredits,
  reportLabel,
  assessmentType: 'process-advisor-ai',
  entitlementType: 'assessment_credit',
  sample: context.sample || context.isSample || context.sampleDataset || 'N',
  sampleDataset: Boolean(context.sampleDataset || isSampleDataset(context)),
});

const getBackendAssessmentAuthorization = async ({ invoke, context = getAiBillingAccessContext(), requirement = getAssessmentCreditRequirement(context), reportLabel = 'AI content' } = {}) => {
  if (typeof invoke !== 'function') return null;
  try {
    const result = await invoke('getAssessmentCreditAuthorization', buildAssessmentAuthorizationPayload({ context, requirement, reportLabel }));
    const authorized = result?.authorized === true || result?.allowed === true;
    if (!authorized) return null;
    return {
      allowed: true,
      authorized: true,
      source: result?.source || 'stored-forge-authorization',
      requirement,
      marker: result?.marker || null,
      authorizationId: result?.authorizationId || result?.marker?.authorizationId || null,
      authorizedCreditCount: result?.authorizedCreditCount || result?.marker?.authorizedCreditCount || requirement.requiredCredits,
      result,
    };
  } catch (error) {
    // Older backends will not have this resolver yet. In that case, fall back to
    // the billing prompt + authorize flow rather than blocking AI generation.
    console.warn('[Metricus billing] Stored authorization check unavailable', error?.message || error);
    return null;
  }
};

export const getBillingCreditStatus = async (invoke) => {
  if (typeof invoke !== 'function') {
    return normalizeBillingStatus({ configured: false, active: false, availableAssessmentCredits: 0, error: 'Billing integration is not available.' });
  }
  try {
    const status = await invoke('getBillingCreditStatus', {});
    return normalizeBillingStatus(status || {});
  } catch (primaryErr) {
    try {
      const status = await invoke('getBillingStatus', {});
      return normalizeBillingStatus(status || {});
    } catch (fallbackErr) {
      return normalizeBillingStatus({
        configured: false,
        active: false,
        availableAssessmentCredits: 0,
        error: fallbackErr?.message || primaryErr?.message || 'Unable to read Metricus billing credits.',
      });
    }
  }
};

const openMetricusBilling = async () => {
  const url = 'https://billing.metricus.com';
  try {
    const bridge = await import('@forge/bridge');
    if (bridge?.router?.open) {
      bridge.router.open(url);
      return;
    }
  } catch (_) {}
  try { window.open(url, '_blank', 'noopener,noreferrer'); } catch (_) {}
};

const showAssessmentCreditPrompt = ({ requirement, status, reportLabel, context }) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const existing = document.getElementById('metricus-assessment-credit-modal');
    if (existing) existing.remove();

    const requiredCredits = requirement.requiredCredits || 1;
    const availableCredits = getAvailableCredits(status);
    const modelTitle = escapeHtml(context?.modelTitle || 'this model');
    const safeReportLabel = escapeHtml(reportLabel || 'AI content');
    const canContinue = availableCredits >= requiredCredits;
    const creditText = `Assessment Credit${requiredCredits === 1 ? '' : 's'}`;

    const overlay = document.createElement('div');
    overlay.id = 'metricus-assessment-credit-modal';
    overlay.setAttribute('role', 'presentation');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(9,30,66,.42);z-index:2147483647;display:flex;align-items:center;justify-content:center;padding:24px;font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;';

    overlay.innerHTML = `
      <div role="dialog" aria-modal="true" aria-labelledby="metricus-credit-title" aria-describedby="metricus-credit-description" style="width:min(640px,100%);background:#fff;border:1px solid #DFE1E6;border-radius:16px;box-shadow:0 18px 48px rgba(9,30,66,.28);overflow:hidden;color:#172B4D;">
        <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;padding:22px 24px;border-bottom:1px solid #EBECF0;background:linear-gradient(180deg,#FFFFFF 0%,#FAFBFC 100%);">
          <div style="display:flex;gap:14px;align-items:flex-start;">
            <div aria-hidden="true" style="width:38px;height:38px;border-radius:12px;background:#E9F2FF;color:#0C66E4;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:18px;flex:0 0 auto;">◆</div>
            <div>
              <div id="metricus-credit-title" style="font-size:19px;font-weight:800;letter-spacing:-.01em;margin:0 0 4px;">Unlock AI content for this model</div>
              <div id="metricus-credit-description" style="font-size:13px;color:#6B778C;line-height:1.45;">Assessment Credits unlock the full AI Advisor outputs for this process model.</div>
            </div>
          </div>
          <button data-action="cancel" aria-label="Close" style="border:0;background:transparent;color:#6B778C;font-size:24px;line-height:1;cursor:pointer;padding:2px 4px;">×</button>
        </div>
        <div style="padding:20px 24px 18px;color:#344563;font-size:14px;line-height:1.55;">
          <div style="border:1px solid #CCE0FF;background:#F4F8FF;border-radius:12px;padding:14px 16px;margin:0 0 16px;">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
              <span aria-hidden="true" style="width:20px;height:20px;border-radius:999px;background:#0C66E4;color:#fff;display:inline-flex;align-items:center;justify-content:center;font-size:12px;font-weight:850;">✓</span>
              <strong style="font-size:15px;color:#172B4D;">Authorise once. Unlock full AI content.</strong>
            </div>
            <div style="font-size:13px;color:#344563;line-height:1.5;">
              Quick Healthcheck can run on the Free Plan. To use the other AI Advisor outputs for <strong>${modelTitle}</strong>, authorise <strong>${formatRecordCount(requiredCredits)} ${creditText}</strong>. This unlock is valid for 60 days from the date it is applied.
            </div>
          </div>

          <p style="margin:0 0 12px;color:#6B778C;font-size:13px;">Current request: <strong style="color:#344563;">${safeReportLabel}</strong></p>

          <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin:16px 0;">
            <div style="border:1px solid #EBECF0;border-radius:10px;padding:13px 14px;background:#F7F8F9;">
              <div style="font-size:11px;text-transform:uppercase;color:#6B778C;font-weight:800;letter-spacing:.05em;">Available</div>
              <div style="font-size:24px;font-weight:850;margin-top:5px;color:#172B4D;">${formatRecordCount(availableCredits)}</div>
              <div style="font-size:12px;color:#6B778C;margin-top:2px;">credits</div>
            </div>
            <div style="border:1px solid #EBECF0;border-radius:10px;padding:13px 14px;background:#F7F8F9;">
              <div style="font-size:11px;text-transform:uppercase;color:#6B778C;font-weight:800;letter-spacing:.05em;">Required</div>
              <div style="font-size:24px;font-weight:850;margin-top:5px;color:#172B4D;">${formatRecordCount(requiredCredits)}</div>
              <div style="font-size:12px;color:#6B778C;margin-top:2px;">now</div>
            </div>
            <div style="border:1px solid #EBECF0;border-radius:10px;padding:13px 14px;background:#F7F8F9;">
              <div style="font-size:11px;text-transform:uppercase;color:#6B778C;font-weight:800;letter-spacing:.05em;">Model size</div>
              <div style="font-size:24px;font-weight:850;margin-top:5px;color:#172B4D;">${formatRecordCount(requirement.recordCount)}</div>
              <div style="font-size:12px;color:#6B778C;margin-top:2px;">work items/tasks</div>
            </div>
          </div>

          <div style="display:flex;gap:10px;align-items:flex-start;border:1px solid #CCE0FF;background:#F4F8FF;border-radius:10px;padding:12px 14px;margin:2px 0 0;">
            <div aria-hidden="true" style="color:#0C66E4;font-weight:800;line-height:1.3;">i</div>
            <div style="font-size:13px;color:#344563;line-height:1.45;">
              Credits are calculated at <strong>1 credit per ${formatRecordCount(ASSESSMENT_RECORD_LIMIT)} work items/tasks</strong> and saved against this process model.
            </div>
          </div>
          ${canContinue ? '' : '<p style="margin:14px 0 0;color:#C9372C;font-weight:700;">There are not enough Assessment Credits available for this model. Use Access & Billing on the app header to enter purchased Assessment Credit keys, then try again.</p>'}
        </div>
        <div style="display:flex;justify-content:flex-end;gap:10px;padding:0 24px 24px;">
          <button data-action="cancel" style="border:1px solid #DFE1E6;background:#fff;color:#172B4D;border-radius:8px;padding:9px 14px;font-size:13px;font-weight:650;cursor:pointer;">Cancel</button>
          <button data-action="confirm" ${canContinue ? '' : 'disabled'} style="border:1px solid ${canContinue ? '#0C66E4' : '#DFE1E6'};background:${canContinue ? '#0C66E4' : '#F4F5F7'};color:${canContinue ? '#fff' : '#A5ADBA'};border-radius:8px;padding:9px 14px;font-size:13px;font-weight:800;cursor:${canContinue ? 'pointer' : 'not-allowed'};box-shadow:${canContinue ? '0 1px 1px rgba(9,30,66,.16)' : 'none'};">Authorise ${formatRecordCount(requiredCredits)} Credit${requiredCredits === 1 ? '' : 's'}</button>
        </div>
      </div>`;

    const previousActiveElement = document.activeElement;
    const cleanup = (value) => {
      document.removeEventListener('keydown', handleKeyDown);
      overlay.remove();
      try { previousActiveElement?.focus?.(); } catch (_) {}
      resolve(value);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cleanup(false);
      }
    };
    overlay.addEventListener('click', (event) => {
      const target = event.target;
      const action = target?.getAttribute?.('data-action');
      if (action === 'cancel') cleanup(false);
      if (action === 'billing') {
        cleanup(false);
      }
      if (action === 'confirm' && canContinue) cleanup(true);
      if (target === overlay) cleanup(false);
    });
    document.addEventListener('keydown', handleKeyDown);
    document.body.appendChild(overlay);
    try { overlay.querySelector('[data-action="confirm"]')?.focus?.(); } catch (_) {}
  });
};

export const authorizeAssessmentCredit = async ({ invoke, reportLabel = 'AI content' } = {}) => {
  const context = getAiBillingAccessContext();
  const requirement = getAssessmentCreditRequirement(context);

  if (!requirement.requiresBilling) {
    return { allowed: true, source: 'marketplace-license-or-free-plan', requirement };
  }

  if (isQuickHealthcheckRequest(reportLabel)) {
    return {
      allowed: true,
      source: 'free-plan-quick-healthcheck',
      quickHealthcheckFree: true,
      requirement,
    };
  }

  // Do not let browser/localStorage markers bypass the credit check.
  // Local markers are only a UI cache; the Forge backend marker is the
  // authority because it survives refreshes and is tied to the saved billing
  // credentials. An older build could have written a local marker before the
  // backend marker existed, which caused AI generation to start without
  // showing the Assessment Credit confirmation dialog.
  const localStored = getStoredAssessmentAuthorization(context);
  if (localStored?.authorized === true) {
    console.log('[Metricus billing] Local assessment authorization found; verifying backend marker before allowing AI generation.', {
      modelId: context?.modelId || '',
      requiredCredits: requirement.requiredCredits,
    });
  }

  const backendAuthorization = await getBackendAssessmentAuthorization({ invoke, context, requirement, reportLabel });
  if (backendAuthorization?.authorized === true || backendAuthorization?.allowed === true) {
    console.log('[Metricus billing] Backend assessment authorization already exists; skipping credit prompt.', {
      modelId: context?.modelId || '',
      requiredCredits: requirement.requiredCredits,
      source: backendAuthorization.source,
    });
    storeAssessmentAuthorization(backendAuthorization, context, requirement);
    return backendAuthorization;
  }

  const status = await getBillingCreditStatus(invoke);

  if (isFullAppAccess(status)) {
    return {
      allowed: true,
      source: status?.billingAccess?.appAccess?.source || status?.appAccess?.source || 'app-level-subscription',
      fullAppAccess: true,
      requirement,
      billingAccess: status.billingAccess || null,
    };
  }

  const availableCredits = getAvailableCredits(status);

  console.log('[Metricus billing] Assessment Credit confirmation required', {
    modelId: context?.modelId || '',
    modelTitle: context?.modelTitle || '',
    recordCount: requirement.recordCount,
    requiredCredits: requirement.requiredCredits,
    availableCredits,
  });

  const confirmed = await showAssessmentCreditPrompt({ requirement, status, reportLabel, context });
  if (!confirmed) {
    if (!status.configured || availableCredits < requirement.requiredCredits) {
      throw new Error('Assessment Credits are required for this AI output. Use Access & Billing on the app header to enter purchased Assessment Credit keys, then try again.');
    }
    throw new Error('AI generation cancelled. Assessment Credit authorisation was not confirmed.');
  }

  if (!status.configured) {
    throw new Error(status.error || 'No Assessment Credit keys are configured. Use Access & Billing on the app header to enter the purchased Assessment Credit keys, then try again.');
  }
  // Do not block here based only on the displayed status balance.
  // The billing API is the source of truth and will validate/consume the
  // purchased Assessment Credit key during authorisation.
  const payload = buildAssessmentAuthorizationPayload({ context, requirement, reportLabel });

  console.log('[Metricus billing] Authorising Assessment Credits with Forge backend', {
    modelId: payload.modelId,
    recordCount: payload.recordCount,
    requiredCredits: payload.requiredCredits,
  });

  const result = await invoke('authorizeAssessmentCredit', payload);
  if (result?.allowed === false) {
    throw new Error(result.message || 'No Assessment Credits are available for this Metricus billing account.');
  }

  const authorization = { allowed: true, source: 'assessment-credit', requirement, ...(result || {}) };
  storeAssessmentAuthorization(authorization, context, requirement);

  // Let the running app know that this model is now fully unlocked. The
  // Process Miner reloads the saved event log without the Free Plan 100-item
  // view, and the header hides the Free Plan / Access & Billing pills for the
  // authorised model.
  if (typeof window !== 'undefined') {
    try {
      window.__PROCESS_ADVISOR_ASSESSMENT_CREDIT_UNLOCKS__ = {
        ...(window.__PROCESS_ADVISOR_ASSESSMENT_CREDIT_UNLOCKS__ || {}),
        [String(context?.modelId || context?.processModelId || '')]: {
          authorized: true,
          modelId: context?.modelId || context?.processModelId || '',
          modelTitle: context?.modelTitle || '',
          recordCount: requirement?.recordCount || 0,
          requiredCredits: requirement?.requiredCredits || 1,
          authorizationId: authorization?.authorizationId || authorization?.marker?.authorizationId || null,
          authorizedAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + ASSESSMENT_CREDIT_VALID_MS).toISOString(),
        },
      };
      window.dispatchEvent(new CustomEvent('metricus-assessment-credit-authorized', {
        detail: {
          modelId: context?.modelId || context?.processModelId || '',
          modelTitle: context?.modelTitle || '',
          recordCount: requirement?.recordCount || 0,
          requiredCredits: requirement?.requiredCredits || 1,
          authorization,
        },
      }));
    } catch (_) {}
  }

  // Refresh the displayed balance after a successful authorisation.
  try { await getBillingCreditStatus(invoke); } catch (_) {}

  return authorization;
};



const isAssessmentCreditOnlyStatus = (status = {}) => {
  const values = [
    status?.entitlementType,
    status?.platform,
    status?.billingAccess?.assessmentCredits?.credentialType,
    status?.assessmentCreditsAccess?.credentialType,
  ].map((v) => String(v || '').trim().toLowerCase());
  return values.some((v) => v === 'assessment_credits' || v === 'assessment-credit' || v === 'assessment credits');
};

export const authorizeAssessmentCreditForCurrentDataset = async ({ invoke, reportLabel = 'Model unlock' } = {}) => {
  if (typeof invoke !== 'function') throw new Error('Billing integration is not available.');
  const context = getAiBillingAccessContext();
  const requirement = getAssessmentCreditRequirement(context);
  const effectiveRequirement = {
    ...requirement,
    requiresBilling: true,
    requiredCredits: requirement.requiredCredits || getRequiredAssessmentCredits(context?.recordCount || 0),
  };

  if (effectiveRequirement.sampleDataset) {
    return { allowed: true, authorized: true, source: 'sample-model', requirement: effectiveRequirement };
  }

  const status = await getBillingCreditStatus(invoke);
  if (isFullAppAccess(status) && !isAssessmentCreditOnlyStatus(status)) {
    return {
      allowed: true,
      authorized: true,
      source: status?.billingAccess?.appAccess?.source || status?.appAccess?.source || 'app-level-subscription',
      fullAppAccess: true,
      requirement: effectiveRequirement,
      billingAccess: status.billingAccess || null,
    };
  }

  if (!status.configured) {
    throw new Error(status.error || 'Assessment Credit keys were not accepted. Check the public integration key and Assessment Credit key.');
  }

  // Do not block here based only on the balance returned by getBillingCreditStatus.
  // Some billing backends validate the newly saved Assessment Credit keys during
  // the authorisation call itself. The source of truth is authorizeAssessmentCredit:
  // if the keys are valid and credits are available it will consume/apply the credit;
  // if not, it returns the real billing error.
  const payload = { ...buildAssessmentAuthorizationPayload({ context, requirement: effectiveRequirement, reportLabel }), forceBillingAuthorization: true };
  let result;
  try {
    result = await invoke('authorizeAssessmentCredit', payload);
  } catch (error) {
    throw new Error(error?.message || 'Unable to apply the Assessment Credit to this model.');
  }
  if (result?.allowed === false || result?.authorized === false) {
    throw new Error(result.message || result.error || 'No Assessment Credits are available for this Metricus billing account.');
  }

  const authorization = { allowed: true, authorized: true, source: 'assessment-credit', requirement: effectiveRequirement, ...(result || {}) };
  storeAssessmentAuthorization(authorization, context, effectiveRequirement);

  if (typeof window !== 'undefined') {
    try {
      window.__PROCESS_ADVISOR_ASSESSMENT_CREDIT_UNLOCKS__ = {
        ...(window.__PROCESS_ADVISOR_ASSESSMENT_CREDIT_UNLOCKS__ || {}),
        [String(context?.modelId || context?.processModelId || '')]: {
          authorized: true,
          modelId: context?.modelId || context?.processModelId || '',
          modelTitle: context?.modelTitle || '',
          recordCount: effectiveRequirement.recordCount || 0,
          requiredCredits: effectiveRequirement.requiredCredits || 1,
          authorizationId: authorization?.authorizationId || authorization?.marker?.authorizationId || null,
          authorizedAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + ASSESSMENT_CREDIT_VALID_MS).toISOString(),
        },
      };
      window.dispatchEvent(new CustomEvent('metricus-assessment-credit-authorized', {
        detail: {
          modelId: context?.modelId || context?.processModelId || '',
          modelTitle: context?.modelTitle || '',
          recordCount: effectiveRequirement.recordCount || 0,
          requiredCredits: effectiveRequirement.requiredCredits || 1,
          authorization,
        },
      }));
    } catch (_) {}
  }

  try { await getBillingCreditStatus(invoke); } catch (_) {}
  return authorization;
};

export const invokeProcessOpenAIWithBilling = async ({ invoke, payload = {}, reportLabel = 'AI content' } = {}) => {
  if (typeof invoke !== 'function') throw new Error('Billing integration is not available.');

  const diagnostic = {
    reportLabel,
    provider: payload?.aiProvider || payload?.provider || null,
    model: payload?.model || null,
    temperature: payload?.temperature,
    promptLength: payload?.prompt?.length || 0,
    hasSystem: Boolean(payload?.system),
    hasUser: Boolean(payload?.user),
    hasSchema: Boolean(payload?.schema),
  };

  console.log('[AI FRONTEND] Starting AI request', diagnostic);

  // Jira Marketplace editions use Forge licensing, not Metricus assessment credits.
  // Preserve the existing credit authorisation flow for non-Jira hosts.
  const jiraEdition = typeof window !== 'undefined'
    ? window.__METRICUS_JIRA_PRODUCT_EDITION__
    : undefined;
  if (jiraEdition === 'free' || jiraEdition === 'enterprise') {
    console.log('[AI FRONTEND] Jira Marketplace request: invoking Forge directly', {
      edition: jiraEdition,
      ...diagnostic,
    });
    try {
      const response = await withAiInvokeTimeout(
        invoke('processOpenAI', payload),
        'Forge processOpenAI resolver'
      );
      if (!response?.requestId) {
        throw new Error('The Forge AI resolver returned without a request ID.');
      }
      return response;
    } catch (error) {
      console.error('[AI FRONTEND] Jira Marketplace AI request failed', {
        edition: jiraEdition,
        reportLabel,
        message: error?.message || String(error),
      });
      throw error;
    }
  }

  try {
    console.log('[AI FRONTEND] Authorising billing access', { reportLabel });
    const authorization = await withAiInvokeTimeout(
      authorizeAssessmentCredit({ invoke, reportLabel }),
      'Assessment-credit authorisation'
    );
    console.log('[AI FRONTEND] Billing access authorised', {
      reportLabel,
      source: authorization?.source || null,
      allowed: authorization?.allowed,
      authorized: authorization?.authorized,
    });

    const invokePayload = {
      ...payload,
      billingAuthorization: authorization?.source && !['marketplace-license-or-free-plan', 'free-plan-quick-healthcheck'].includes(authorization.source)
        ? {
            source: authorization.source,
            modelId: getAiBillingAccessContext().modelId || '',
            recordCount: authorization.requirement?.recordCount || 0,
            recordLimit: authorization.requirement?.recordLimit || ASSESSMENT_RECORD_LIMIT,
            requiredCredits: authorization.requirement?.requiredCredits || 1,
            assessmentCreditId: authorization.assessmentCreditId || authorization.creditId || null,
          }
        : undefined,
    };

    console.log('[AI FRONTEND] Invoking processOpenAI', diagnostic);
    const response = await withAiInvokeTimeout(
      invoke('processOpenAI', invokePayload),
      'Forge processOpenAI resolver'
    );
    console.log('[AI FRONTEND] processOpenAI returned', {
      reportLabel,
      requestId: response?.requestId || null,
      status: response?.status || null,
    });

    if (!response?.requestId) {
      throw new Error('The Forge AI resolver returned without a request ID.');
    }
    return response;
  } catch (error) {
    console.error('[AI FRONTEND] AI request failed before polling', {
      ...diagnostic,
      errorName: error?.name,
      errorMessage: error?.message || String(error),
      stack: error?.stack,
    });
    throw error;
  }
};
